/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {QueryChipData} from '@ion/features/src/search/types';
import {
  activeVehicleTab,
  setActiveVehicleTab,
  setVehicleManagerSearchQueries
} from './manager/store/store';
import {
  activeProfileTab,
  clearProfileManagerProfileLookup,
  setActiveProfileTab,
  setProfileManagerProfileLookup,
  setProfileManagerSearchQueries
} from './profile/store/store';
import {
  UNKNOWN_ENGINE_ID,
  UNKNOWN_FUEL_TYPE_ID,
  UNKNOWN_YEAR_ID,
  YEAR_ID_OFFSET
} from './profile/VehicleProfileEditorUtils';
import {Context, ProfileLookup} from './types';

type VehicleProfileFilterData = {
  year?: string | number;
  yearString?: string;
  make?: string | number;
  makeString?: string;
  model?: string | number;
  modelString?: string;
  engine?: string | number;
  engineString?: string;
  fuelType?: string | number;
  fuelString?: string;
  isTest?: boolean;
  profileEnv?: string;
  profile?: {
    year?: number;
    make?: number;
    model?: number;
    engine?: number;
    fuel?: number;
  };
};

const UNKNOWN_FILTER_VALUES = new Set([
  'unknown',
  'unknown fuel type'
]);

/**
 * Normalizes the given value for modular-search filter creation.
 *
 * @param   value The value to normalize before filter evaluation.
 * @returns The trimmed string value when provided, otherwise `''`.
 */
const normalizeValue = (value?: string | number): string => {
  const normalizedValue = typeof value === 'string' ? value.trim() : '';
  return normalizedValue;
};

/**
 * Creates a modular-search chip when the given value resolves to text.
 *
 * @param   attribute The target search attribute for the chip.
 * @param   value The value to normalize and apply to the chip.
 * @returns A `QueryChipData` object when the value is valid, otherwise `null`.
 */
const createFilter = (attribute: string, value?: string): QueryChipData | null => {
  const trimmedValue = normalizeValue(value);
  if (trimmedValue) {
    return {
      attribute,
      value : trimmedValue,
      label : `${attribute.charAt(0).toUpperCase()}${attribute.slice(1)}: ${trimmedValue}`
    } as QueryChipData;
  }
  return null;
};

/**
 * Resolves a filter value while excluding placeholder display text.
 *
 * @param   value The candidate display value to evaluate.
 * @returns The normalized value when it is usable, otherwise `''`.
 */
const resolveFilterValue = (value?: string | number): string => {
  const normalizedValue = normalizeValue(value);
  const normalizedKey   = normalizedValue.toLowerCase();
  const resolvedValue   = UNKNOWN_FILTER_VALUES.has(normalizedKey) ? '' : normalizedValue;
  return resolvedValue;
};

/**
 * Resolves the first usable display value from the given candidates.
 *
 * @param   primaryValue The preferred display value.
 * @param   secondaryValue The fallback display value.
 * @returns The first normalized display value, otherwise `''`.
 */
const resolveDisplayValue = (primaryValue?: string, secondaryValue?: string | number): string => {
  const preferredValue = resolveFilterValue(primaryValue);
  const fallbackValue  = resolveFilterValue(secondaryValue);
  const resolvedValue  = preferredValue || fallbackValue;
  return resolvedValue;
};

/**
 * Removes null filter entries produced during filter construction.
 *
 * @param   filters The filter list containing chips and null placeholders.
 * @returns A compact array containing only valid `QueryChipData` items.
 */
const compactFilters = (filters: Array<QueryChipData | null>): QueryChipData[] => {
  return filters.filter((filter): filter is QueryChipData => Boolean(filter));
};

/**
 * Resolves the navigation environment from either row metadata or display text.
 *
 * @param data Vehicle/profile row data used by cross-manager navigation.
 * @returns The matching manager tab context when it can be determined.
 */
const resolveNavigationEnvironment = (
  data: VehicleProfileFilterData | undefined,
  fallback: Context
): Context => {
  const {test, production} = Context;
  const profileEnv         = (normalizeValue(data?.profileEnv) ?? '').toLowerCase();
  if (data?.isTest === true || profileEnv === test) {
    return test;
  }
  if (data?.isTest === false || profileEnv) {
    return production;
  }
  return fallback;
};

/**
 * Creates Vehicle Manager modular-search filters from a VIN value.
 *
 * ```typescript
 * import {createVehicleManagerFiltersFromVin} from
 *   "@ion/applications/src/pages/vehicle/VehicleNavigationUtils";
 * ...
 *
 * const filters = createVehicleManagerFiltersFromVin('1FDP90017180000000');
 * ```
 *
 * @param   vin The VIN value to convert into Vehicle Manager filters.
 * @returns A list of modular-search chips for the given VIN.
 */
export const createVehicleManagerFiltersFromVin = (vin?: string): QueryChipData[] => {
  return compactFilters([
    createFilter('vin', normalizeValue(vin))
  ]);
};

/**
 * Creates Vehicle Manager modular-search filters from vehicle/profile
 * display values.
 *
 * ```typescript
 * import {createVehicleManagerFiltersFromVehicle} from
 *   "@ion/applications/src/pages/vehicle/VehicleNavigationUtils";
 * ...
 *
 * const filters = createVehicleManagerFiltersFromVehicle({
 *   yearString   : '2024',
 *   makeString   : 'LAMBORGHINI',
 *   modelString  : 'HURACAN',
 *   engineString : '5.2L Nat Aspirated V10',
 *   fuelString   : 'Petroleum (Gasoline)'
 * });
 * ```
 *
 * @param   vehicle The vehicle/profile display values to convert into
 *          Vehicle Manager filters.
 * @returns A list of modular-search chips for Vehicle Manager.
 */
export const createVehicleManagerFiltersFromVehicle = (vehicle?: VehicleProfileFilterData): QueryChipData[] => {
  const yearValue   = resolveDisplayValue(vehicle?.yearString, vehicle?.year);
  const makeValue   = resolveDisplayValue(vehicle?.makeString, vehicle?.make);
  const modelValue  = resolveDisplayValue(vehicle?.modelString, vehicle?.model);
  const engineValue = resolveDisplayValue(vehicle?.engineString, vehicle?.engine);
  const fuelValue   = resolveDisplayValue(vehicle?.fuelString, vehicle?.fuelType);
  return compactFilters([
    createFilter('year', yearValue),
    createFilter('make', makeValue),
    createFilter('model', modelValue),
    createFilter('engine', engineValue),
    createFilter('fuel', fuelValue)
  ]);
};

/**
 * Creates Profile Manager modular-search filters from normalized
 * vehicle row values.
 *
 * ```typescript
 * import {createProfileManagerFiltersFromVehicle} from
 *   "@ion/applications/src/pages/vehicle/VehicleNavigationUtils";
 * ...
 *
 * const filters = createProfileManagerFiltersFromVehicle({
 *   year     : '2024',
 *   make     : 'LAMBORGHINI',
 *   model    : 'HURACAN',
 *   engine   : '5.2L Nat Aspirated V10',
 *   fuelType : 'Petroleum (Gasoline)'
 * });
 * ```
 *
 * @param   vehicle The normalized vehicle row values to convert into
 *          Profile Manager filters.
 * @returns A list of modular-search chips for Profile Manager.
 */
export const createProfileManagerFiltersFromVehicle = (
  vehicle?: VehicleProfileFilterData
): QueryChipData[] => {
  return createVehicleManagerFiltersFromVehicle(vehicle);
};

/**
 * Sets Vehicle Manager search filters from a VIN.
 *
 * @param vin The VIN value used to seed Vehicle Manager filters.
 */
export const setVehicleManagerFiltersFromVin = (vin?: string): void => {
  const filters = createVehicleManagerFiltersFromVin(vin);

  if (filters.length) {
    setVehicleManagerSearchQueries?.(filters as any);
  }
};

/**
 * Sets Vehicle Manager search filters from a vehicle/profile object.
 *
 * @param vehicle The vehicle/profile data used to seed Vehicle Manager filters.
 */
export const setVehicleManagerFiltersFromVehicle = (vehicle?: VehicleProfileFilterData): void => {
  const filters = createVehicleManagerFiltersFromVehicle(vehicle);
  const environment = resolveNavigationEnvironment(vehicle, activeVehicleTab.value);
  setActiveVehicleTab(environment);
  if (filters.length) {
    setVehicleManagerSearchQueries?.(filters as any);
  }
};

/**
 * Sets Profile Manager search filters from a vehicle object.
 *
 * @param vehicle The vehicle data used to seed Profile Manager filters.
 */
export const setProfileManagerFiltersFromVehicle = (vehicle?: VehicleProfileFilterData): void => {
  const filters = createProfileManagerFiltersFromVehicle(vehicle);
  const environment = resolveNavigationEnvironment(vehicle, activeProfileTab.value);
  setActiveProfileTab(environment);
  if (filters.length) {
    setProfileManagerSearchQueries?.(filters as any);
  }
};

/**
 * Resolves the numeric identifier for the given profile year value.
 *
 * The Vehicle Search API reports a profile's year as a calendar year, while
 * the profiles search APIs key on an offset identifier. Values at or above
 * the offset are converted, and values already below it - including the
 * `UNKNOWN_YEAR_ID` sentinel - are used as given.
 *
 * @param   year The profile year value to resolve.
 * @returns The offset year identifier for the given value.
 */
const resolveProfileYearId = (year: number): number => (
  year >= YEAR_ID_OFFSET ? year - YEAR_ID_OFFSET : year
);

/**
 * Resolves a numeric profile identifier, applying the given fallback when the
 * value is absent or cannot be read as a number.
 *
 * @param   value The candidate identifier to resolve.
 * @param   fallback The identifier to use when the value is unusable.
 * @returns The resolved numeric identifier.
 */
const resolveProfileId = (value: number | undefined, fallback: number): number => {
  const resolvedValue = Number(value ?? fallback);
  return Number.isNaN(resolvedValue) ? fallback : resolvedValue;
};

/**
 * Creates the numeric profile lookup for the given vehicle row.
 *
 * ```typescript
 * import {createProfileLookupFromVehicle} from
 *   "@ion/applications/src/pages/vehicle/VehicleNavigationUtils";
 * ...
 *
 * const lookup = createProfileLookupFromVehicle({
 *   profile : {
 *     year   : 2024,
 *     make   : 209,
 *     model  : 8286,
 *     engine : 4294967295,
 *     fuel   : 15
 *   }
 * });
 * ```
 *
 * @param   vehicle The vehicle row whose profile identifiers are read.
 * @returns The profile lookup when the make and model are known, otherwise
 *          `null`.
 */
export const createProfileLookupFromVehicle = (
  vehicle?: VehicleProfileFilterData
): ProfileLookup | null => {
  const {year, make, model, engine, fuel} = vehicle?.profile || {};
  const makeId  = Number(make);
  const modelId = Number(model);
  if (!Number.isNaN(makeId) && !Number.isNaN(modelId) && make != null && model != null) {
    return {
      year   : resolveProfileYearId(resolveProfileId(year, UNKNOWN_YEAR_ID)),
      make   : makeId,
      model  : modelId,
      engine : resolveProfileId(engine, UNKNOWN_ENGINE_ID),
      fuel   : resolveProfileId(fuel, UNKNOWN_FUEL_TYPE_ID)
    };
  }
  return null;
};

/**
 * Pins Profile Manager to the single profile the given vehicle is associated
 * with, using the vehicle's numeric profile identifiers, and seeds the search
 * chips from the vehicle's display values.
 *
 * Vehicles without usable make and model identifiers are left with the
 * modular-search filters alone, matching the previous navigation behavior.
 *
 * @param vehicle The vehicle data used to resolve the associated profile.
 */
export const setProfileManagerLookupFromVehicle = (vehicle?: VehicleProfileFilterData): void => {
  const lookup = createProfileLookupFromVehicle(vehicle);
  if (lookup) {
    setProfileManagerProfileLookup(lookup);
  } else {
    clearProfileManagerProfileLookup();
  }
  // the vehicle's display values still seed the search chips, so the user can
  // see which vehicle the results belong to and can search on from there...
  setProfileManagerFiltersFromVehicle(vehicle);
};
